/**
 * Imports necessary dependencies and components for the current module.
 * @module
 * @requires React
 * @requires CSmartTable from "@coreui/react-pro"
 * @requires useTranslation from "react-i18next"
 * @requires Card from "react-bootstrap/Card"
 *
 * @requires CreateGenres from "./createGenres"
 *
 * @requires CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML"
 * @requires Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb"
 * @requires ConfirmModal from "../../components/confirmModal/confirmModel"
 * @requires EmptyCell from "../../components/emptyCell/emptyCell"
 */
import { CSmartTable } from "@coreui/react-pro";
import React from "react";
import Card from "react-bootstrap/Card";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";

import CreateGenres from "./createGenres";

import ConfirmModal from "../../components/confirmModal/confirmModel";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomLoadingHTML from "../../components/customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../components/emptyCell/emptyCell";

/**
 * A component for managing genres.
 * @param {{object}} props - The component props.
 * @param {{function}} props.showCreateGenresModal - Function to show the create genres modal.
 * @param {{function}} props.closeCreateGenresModal - Function to close the create genres modal.
 * @param {{function}} props.openCreateGenresModal - Function to open the create genres modal.
 * @param {{array}} props.genresData - The data for the genres.
 * @param {{array}} props.columns - The columns for the genres table.
 * @param {{boolean}} props.placeHolderShimer - Flag indicating if the placeholder shimer is shown.
 * @param {{function}} props.onUpdateGenres - Function to update genres
 */
const GenresManagementComponent = (props) => {
  /**
   * Retrieves the translation function from the useTranslation hook.
   * @returns The translation function.
   */
  const { t } = useTranslation();

  /**
   * Destructures the properties from the given props object.
   * @param {{object}} props - The props object containing the properties to destructure.
   * @returns None
   */
  const {
    showCreateGenresModal,
    closeCreateGenresModal,
    openCreateGenresModal,
    genresData,
    columns,
    placeHolderShimer,
    onUpdateGenres,
    openDeleteUserModal,
    onTableFilterChange,
    NoDataTitle,
    NoDataDesc,
    noDataDescTwo,
    ClearSearchButton,
    modalName,
    showDeleteUserModal,
    onDeleteUser,
    closeDeleteUserModal,
    smarttableref,
    NoDataFound,
    NoDataAddButtonTitle,
    EmptyDataAddButton,
    createGenresModelLoaderButton,
    handleGenrestextChange,
    genrestextcount,
    genrestitletextcount,
    genresdescriptiontextcount,
    createValues,
    createGenresFunction,
    updateGenresFunction,
    updateGenresModelLoaderButton,
    handleGenrestitletextChange,
    handleGenresdescriptiontextChange,

    dimensions,
    breadcrumbHeight,
    searchFilterClear,
    searchValue,
    loading,
    tableContainerRef,
    loaderHtml,
    handleSubmitCreate,
    genresValue,
    genrestitleValue,
    genresdescriptionValue,
    itemsPerPage,
    pageTitle,
    emptyDataDesc,
    emptyTitle,
    emptyImg,
    deleteLoader,
    emptyDataDescTow,
  } = props;

  return (
    <React.Fragment>
      <Helmet>
        <title>Genres Management | RizzTix </title>
      </Helmet>
      <div className="pageContent genresManagement">
        <div className="genres">
          <Breadcrumbs
            innerPagetitle={t("GENRES_MANAGMENT")}
            breadcrumbItem={t("GENRES_MANAGMENT")}
            pageTitle={pageTitle}
            BreadcrumbInner={true}
            BreadcrumbmainTitle={true}
          />
          <Card className="mainCard mainGenres">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                <div className="Card-Button">
                  <button
                    color="primary"
                    className="btn-block btn btn-primary fillButton"
                    onClick={() => openCreateGenresModal()}
                  >
                    <i className="bx bxs-plus-circle"></i>
                    {t("ADD_GENERE")}
                  </button>
                </div>
                {/**
                 * Renders a smart table component with custom column rendering and filtering options.
                 * @param {{boolean}} cleaner - Whether to enable the cleaner feature.
                 * @param {{boolean}} clickableRows - Whether to make the rows clickable.
                 * @param {{Array}} columns - The array of column objects to display in the table.
                 * @param {{boolean}} columnFilter - Whether to enable column filtering.
                 * @param {{Array}} items - The array of items to display in the table.
                 * @param {{number}} itemsPerPage - The number of items to display per page.
                 * @param {{Object}} scopedColumns - An object mapping column names to custom rendering functions.
                 * @param {{function}} onTableFilterChange - The function to call
                 */}
                <CSmartTable
                  ref={smarttableref}
                  cleaner
                  clickableRows
                  columns={columns}
                  columnFilter={false}
                  items={genresData}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    title: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.title}
                          </div>
                        </td>
                      );
                    },
                    titlestring: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.titlestring || "-"}
                          </div>
                        </td>
                      );
                    },
                    shortdesc: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item.shortdesc ? item.shortdesc : "-"}
                          </div>
                        </td>
                      );
                    },
                    eventcount: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.eventcount ? item?.eventcount : 0}
                            {/* <i className="dripicons-chevron-right"></i> */}
                          </div>
                        </td>
                      );
                    },
                    status: (item) => {
                      return (
                        <td
                          className={item.status === "deactive" && "disableTd"}
                        >
                          <div className="actionWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <button
                              className="editButtton"
                              onClick={(e) => {
                                onUpdateGenres(e, item);
                              }}
                            >
                              <i
                                className="mdi mdi-pencil-outline font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                            <button
                              className="deleteButtton"
                              onClick={(e) => {
                                openDeleteUserModal(e, item);
                              }}
                            >
                              <i
                                className="bx bxs-trash font-size-24"
                                id="deletetooltip"
                              ></i>
                            </button>
                          </div>
                        </td>
                      );
                    },
                  }}
                  tableFilter
                  onTableFilterChange={onTableFilterChange}
                  tableFilterLabel={
                    searchValue?.length > 0 && (
                      <button
                        onClick={searchFilterClear}
                        className="btn-close"
                      ></button>
                    )
                  }
                  tableFilterPlaceholder={t("SEARCH_GENRES")}
                  tableProps={{
                    className: "mainTable",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={genresData}
                        noDataTitle={
                          genresData?.length === 0 ? NoDataFound : NoDataTitle
                        }
                        noDataAddButton={
                          genresData?.length === 0 ? true : false
                        }
                        noDataAddButtonTitle={NoDataAddButtonTitle}
                        noDataDesc={NoDataDesc}
                        noDataDescTwo={noDataDescTwo}
                        emptyDataAddButton={EmptyDataAddButton}
                        clearSearchButton={ClearSearchButton}
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        emptyDataDesc={emptyDataDesc}
                        emptyDataDescTow={emptyDataDescTow}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll",
                    style: {
                      height:
                        dimensions.width > 1279
                          ? dimensions.height -
                            breadcrumbHeight -
                            (loading && loaderHtml) -
                            250
                          : dimensions.height -
                            breadcrumbHeight -
                            (loading && loaderHtml) -
                            350,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
        {/* Delete Message Model */}
        <ConfirmModal
          modalTitle={"Delete"}
          modalDescription={t("DELETE_GENRES_HEADING")}
          isUnderline={false}
          showModal={showDeleteUserModal}
          leftBtnTitle={t("YES_DELETE")}
          isModelIcon={true}
          iconColor={"bx bxs-trash"}
          iconCricle={"iconCricleDelete"}
          leftBtnClass={"ok_Btn"}
          isFirstButton={true}
          isSecondButton={true}
          rightBtnClass="borderButton"
          rightBtnTitle={t("NO_CANCEL")}
          onProcced={onDeleteUser}
          rightButtonClick={closeDeleteUserModal}
          loaderButtonShow={deleteLoader}
        />
        <CreateGenres
          placeholder={t("GENRE_PLACEHOLDER")}
          titleplaceholder={t("TITLE_PLACEHOLDER")}
          descriptionplaceholder={t("DESCRIPTION_PLACEHOLDER")}
          label={t("GENRE_NAME")}
          discriptionlabel={t("DESCRIPTION")}
          titlelabel={t("TITLE_LABLE")}
          descriptionlabel={t("DESCRIPTION_LABLE")}
          genresValue={genresValue}
          genrestitleValue={genrestitleValue}
          genresdescriptionValue={genresdescriptionValue}
          modalName={modalName}
          showCreateGenresModal={showCreateGenresModal}
          closeCreateGenresModal={closeCreateGenresModal}
          updateGenresFunction={(form) => updateGenresFunction(form)}
          createGenresModelLoaderButton={createGenresModelLoaderButton}
          handleGenrestextChange={handleGenrestextChange}
          genrestextcount={genrestextcount}
          genrestitletextcount={genrestitletextcount}
          genresdescriptiontextcount={genresdescriptiontextcount}
          updateGenresModelLoaderButton={updateGenresModelLoaderButton}
          createValues={createValues}
          createGenresFunction={createGenresFunction}
          handleSubmitCreate={handleSubmitCreate}
          handleGenrestitletextChange={handleGenrestitletextChange}
          handleGenresdescriptiontextChange={handleGenresdescriptiontextChange}
        />
      </div>
    </React.Fragment>
  );
};

export default GenresManagementComponent;
